import { Suspense, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";

import { useAuth } from "../hooks/useAuth.js";
import { site } from "../lib/site.js";
import { Spinner } from "./States.js";
import { Wordmark } from "./Wordmark.js";

const NAV_LINKS = [
  { to: "/", label: "Home", end: true },
  { to: "/reviews", label: "Reviews", end: false },
  { to: "/ratings", label: "Ratings", end: false },
  { to: "/about", label: "About", end: false },
];

function navClass({ isActive }: { isActive: boolean }): string {
  return [
    "relative py-2 text-sm uppercase tracking-[0.18em] transition-colors",
    isActive ? "text-lamp-400" : "text-paper-400 hover:text-paper-50",
  ].join(" ");
}

export function Layout() {
  const { user, isAdmin, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // No background colour on the wrapper on purpose: `body` already paints
  // ink-900, and an opaque wrapper would sit on top of a page's PageBackdrop.
  return (
    <div className="flex min-h-screen flex-col">
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <header className="sticky top-0 z-50 border-b border-ink-700 bg-ink-900/85 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-4">
          <Link
            to="/"
            className="group shrink-0"
            onClick={() => setMenuOpen(false)}
          >
            {/* Same face, same accent word and same tracking as the hero. */}
            <Wordmark size="sm" />
          </Link>

          <nav
            aria-label="Main"
            className="hidden items-center gap-8 md:flex"
          >
            {NAV_LINKS.map((link) => (
              <NavLink key={link.to} to={link.to} end={link.end} className={navClass}>
                {link.label}
              </NavLink>
            ))}

            {isAdmin && (
              <NavLink to="/admin" className={navClass}>
                Admin
              </NavLink>
            )}
          </nav>

          <div className="hidden items-center gap-4 md:flex">
            {user ? (
              <>
                <span className="text-sm text-paper-400">
                  {user.displayName}
                </span>
                <button
                  type="button"
                  onClick={() => void signOut()}
                  className="text-sm text-paper-400 underline-offset-4 hover:text-paper-50 hover:underline"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/sign-in"
                  state={{ from: location.pathname }}
                  className="py-2 text-sm text-paper-400 hover:text-paper-50"
                >
                  Sign in
                </Link>
                <Link
                  to="/sign-up"
                  className="rounded-sm bg-lamp-400 px-3.5 py-1.5 text-sm font-semibold text-ink-950 transition-colors hover:bg-lamp-300"
                >
                  Join
                </Link>
              </>
            )}
          </div>

          <button
            type="button"
            className="md:hidden"
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <svg
              viewBox="0 0 24 24"
              className="h-6 w-6 text-paper-50"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              aria-hidden="true"
            >
              {menuOpen ? (
                <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
              ) : (
                <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
              )}
            </svg>
          </button>
        </div>

        {menuOpen && (
          <nav
            id="mobile-nav"
            aria-label="Main"
            className="border-t border-ink-700 px-5 py-4 md:hidden"
          >
            <ul className="flex flex-col gap-3">
              {NAV_LINKS.map((link) => (
                <li key={link.to}>
                  <NavLink
                    to={link.to}
                    end={link.end}
                    className={navClass}
                    onClick={() => setMenuOpen(false)}
                  >
                    {link.label}
                  </NavLink>
                </li>
              ))}

              {isAdmin && (
                <li>
                  <NavLink
                    to="/admin"
                    className={navClass}
                    onClick={() => setMenuOpen(false)}
                  >
                    Admin
                  </NavLink>
                </li>
              )}

              <li className="mt-2 border-t border-ink-700 pt-3">
                {user ? (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      void signOut();
                    }}
                    className="text-sm text-paper-400"
                  >
                    Sign out ({user.displayName})
                  </button>
                ) : (
                  <div className="flex gap-4">
                    <Link
                      to="/sign-in"
                      className="text-sm text-paper-400"
                      onClick={() => setMenuOpen(false)}
                    >
                      Sign in
                    </Link>
                    <Link
                      to="/sign-up"
                      className="text-sm font-semibold text-lamp-400"
                      onClick={() => setMenuOpen(false)}
                    >
                      Join
                    </Link>
                  </div>
                )}
              </li>
            </ul>
          </nav>
        )}
      </header>

      {/*
        The Suspense boundary sits here, not around the router, so that a
        lazily-loaded route suspends the page area only. With it further out the
        header and footer unmounted on every navigation and the whole chrome
        flashed. `min-h-[60vh]` keeps the footer from jumping up the screen
        while a chunk is in flight.
      */}
      <main id="main" className="flex-1">
        <Suspense
          fallback={
            <div className="min-h-[60vh]">
              <Spinner />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </main>

      <footer className="mt-24 border-t border-ink-700 bg-ink-950">
        <div className="mx-auto grid max-w-6xl gap-8 px-5 py-12 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <Wordmark size="md" />
            <p className="mt-2 max-w-xs text-sm leading-relaxed text-paper-400">
              {site.tagline} by {site.author}, an independent filmmaker.
            </p>
          </div>

          <div>
            <h2 className="text-xs uppercase tracking-[0.2em] text-paper-500">
              Elsewhere
            </h2>
            <ul className="mt-3 space-y-2 text-sm">
              <li>
                <a
                  href={site.contact.letterboxdUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-block py-1 text-paper-200 underline-offset-4 hover:text-lamp-400 hover:underline"
                >
                  Letterboxd — {site.contact.letterboxd}
                </a>
              </li>
              <li>
                <a
                  href={site.contact.instagramUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-block py-1 text-paper-200 underline-offset-4 hover:text-lamp-400 hover:underline"
                >
                  Instagram — {site.contact.instagram}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xs uppercase tracking-[0.2em] text-paper-500">
              Get in touch
            </h2>
            <ul className="mt-3 space-y-2 text-sm">
              <li>
                <a
                  href={`mailto:${site.contact.email}`}
                  className="inline-block py-1 text-paper-200 underline-offset-4 hover:text-lamp-400 hover:underline"
                >
                  {site.contact.email}
                </a>
              </li>
              <li>
                <a
                  href={`tel:${site.contact.phoneHref}`}
                  className="inline-block py-1 text-paper-200 underline-offset-4 hover:text-lamp-400 hover:underline"
                >
                  {site.contact.phone}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/*
          The legal links sit in the bottom bar rather than in a column of
          their own above it. They are the two links on the site nobody is
          looking for until they are, and giving them a heading in the grid
          would have put "Legal" at the same weight as the reviews.
        */}
        <div className="border-t border-ink-800 px-5 py-5">
          <div className="mx-auto flex max-w-6xl flex-col items-center gap-3 text-xs text-paper-500 sm:flex-row sm:justify-between">
            <p>
              © {new Date().getFullYear()} {site.brand}. All reviews written by{" "}
              {site.author}.
            </p>

            <nav aria-label="Legal">
              <ul className="flex items-center gap-5">
                <li>
                  <Link
                    to="/terms"
                    className="inline-block py-1 underline-offset-4 hover:text-paper-200 hover:underline"
                  >
                    Terms
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy"
                    className="inline-block py-1 underline-offset-4 hover:text-paper-200 hover:underline"
                  >
                    Privacy
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
